/* eslint-disable indent */
/* eslint-disable no-confusing-arrow */
import { color, utils, Common, Orderbook } from '@stockbit/ui';
import styled, { css } from 'styled-components';

const { Text, Flex, IconMoreOptions, IconWrapper } = Common;
const { Table } = Orderbook;
const { hexOpacity } = utils;

interface OrderbookTableWrapperProps {
  $isHidden?: boolean;
  $isIHSG?: boolean;
}

const colorTextByType = {
  up: color.primary,
  down: color.sunsetOrange,
  notchange: color.highball,
};

const OrderbookText = styled(Text).attrs((props) => ({
  size: '12.5px',
  align: 'right',
  padding: props.padding || '5px 3px',
  height: '1.15',
  type: props.type,
}))`
  color: ${({ type }) => colorTextByType[type]};
`;

const OrderbookTextHover = styled(OrderbookText).attrs((props) => ({
  bid: props.bid,
}))`
  cursor: pointer;
  :hover {
    background: ${({ bid }) =>
      bid
        ? color.primary + hexOpacity(30)
        : color.sunsetOrange + hexOpacity(30)};
  }
`;

const CardHeader = styled(Flex).attrs({ padding: '9px 12px', align: 'center' })`
  position: relative;
  border-bottom: 1px solid ${color.gainsboro};
  padding: 20px 16px;
`;

const Tag = styled(Text).attrs({
  height: '20px',
  size: '12.5px',
  color: color.light,
  padding: '0 5px',
  width: 'auto',
  family: 'bold',
})`
  background: ${color.highempahis};
  border-radius: 2px;
`;

const OptionText = styled.div`
  margin-left: 10px;
  display: inline-block;
  font-size: 13px;
  text-overflow: ellipsis;
  max-width: calc(100% - 10px);
  overflow: hidden;
`;

const NotAvailableText = styled(Text).attrs({
  color: color.sunsetOrange,
  padding: '2px 5px',
})`
  border: 1px solid ${color.sunsetOrange};
  border-radius: 2px;
`;

const RightIconMoreOptions = styled(IconMoreOptions).attrs({ vertical: true })`
  position: absolute;
  right: 10px;
`;

const TableSeparatorWrapper = styled.table`
  margin: auto;
  position: relative;
  table-layout: fixed;
  width: 100%;
  z-index: 1;
  background: ${color.aliceBlue};
  border-top: 1px solid ${color.gainsboro};
  border-bottom: 1px solid ${color.gainsboro};

  thead {
    th {
      height: 32px;
      line-height: 16px;
      font-weight: normal;
      text-align: center;
      padding: 8px 0px;
    }
  }
`;

const TableBestBidOfferWrapper = styled(TableSeparatorWrapper)`
  background: unset;
  height: 32px;
  border: none;

  tbody {
    td {
      height: 20px;
      line-height: 16px;
      font-weight: normal;
      text-align: center;
      padding: 4px 0px;
    }

    tr {
      border: 5px solid ${color.downgray};
    }
  }
`;

const OrderbookTableWrapper = styled.div<OrderbookTableWrapperProps>`
  user-select: none;
  
  .empty-table > tbody {
    display: table;
  }

  table {
    margin: auto;
    position: relative;
    table-layout: fixed;
    width: 100%;
    z-index: 1;
  }

  tbody {
    display: block;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 320px;
  }

  thead, tbody tr, tfoot {
    display: table;
    width: 321px;
    table-layout: fixed;
  }

  thead {
    th {
      height: 32px;
      line-height: 16px;
      font-weight: normal;
      text-align: center;
      padding: 8px 0px;
    }
  } 

  tbody {
    > tr:hover {
      > td {
        background: unset;
      }
    }
    
    td {
      text-align: center;
      padding: 8px 0px;
      white-space: nowrap;
    }

    td:first-child, td:last-child {
      width: 40px;
    }

    td:nth-child(2), td:nth-child(5) {
      width: 66.5px;
    }

    td:nth-child(3), td:nth-child(4) {
      width: 50px;
    }
  } 

  tfoot {
    background: ${color.aliceBlue};
    border-top: 1px solid ${color.gainsboro};

    > tr {
      > td {
        border-bottom: none;
        height: 32px;
        line-height: 16px;
        font-weight: normal;
        text-align: center;
        padding: 8px 0px;
      }
    }

    td:nth-child(3) {
      width: 100px;
    }

    td:first-child, td:last-child {
      width: 40px;
    }

    td:nth-child(2), td:nth-child(4) {
      width: 66.5px;
    }
  }

  tr.blurred {
    opacity: 0.4;
  }

  .highlited-bid::before,
  .highlited-off::before {
    position: absolute;
    content: '';
    width: 200%;
    height: 100%;
  }

  .highlited-bid::before {
    background: ${color.primary + hexOpacity(30)};
  }

  td:nth-child(2):before {
    left: -50px;
  }

  .highlited-off::before {
    background: ${color.sunsetOrange + hexOpacity(30)};
  }

  td:nth-child(4):before {
    right: -5px;
  }

  ${(props) => props.$isHidden &&
  css`
    padding-top: 25px;

    ::before {
      border-bottom: none;
    }
  `}

  ${(props) => props.$isIHSG &&
  css`
    ::after,
    ::before {
      display: none;
    }
  `}
`;

const SecureTable = styled(Table).attrs({
  padding: '0',
  showHeader: false,
})`
  user-select: none;

  .ant-table-tbody {
    > tr:hover {
      > td {
        background: unset;
      }
    }

    td {
      white-space: nowrap;
    }
  }
`;

const BalanceInfoWrapper = styled(Flex).attrs({
  align: 'center',
  height: '25px',
})`
  background: ${color.aliceBlue};
  border-bottom: 1px solid ${color.gainsboro};
`;

const StyledIconWrapper = styled(IconWrapper).attrs({ size: '24px' })`
  background: ${color.light};
  border-right: 1px solid ${color.gainsboro};
`;

const ValueWrapper = styled(OrderbookText).attrs({
  align: 'center',
  py: '4px',
})`
  background: ${color.light};
  border: solid ${color.gainsboro};
  border-width: 0 1px;
  min-width: 50px;
  margin: 0 7px;
`;

interface PendingMarkProps {
  isBuy?: boolean;
  show?: boolean;
}
// eslint-disable-next-line space-infix-ops
const PendingMark = styled.div<PendingMarkProps>`
  background: ${({ isBuy }) => (isBuy ? color.primary : color.sunsetOrange)};
  display: ${({ show }) => (show ? 'block' : 'none')};
  width: 6px;
  height: 6px;
  border-radius: 50%;
  cursor: pointer;
  position: absolute;
  top: 40%;
  ${({ isBuy }) => (isBuy ? 'left: -20px' : 'right: -20px')};
`;

const Wrapper = styled.div`
  background-color: ${color.light};
  border: solid 1px ${color.gainsboro};
  margin-bottom: 20px;
  position: relative;

  table.table-live-price {
    td:first-child {
      padding-left: 0px;
      text-align: left;
      width: 30px;
    }

    td:nth-child(even) {
      padding-right: 12px;

      &.two-column {
        padding-right: 16px;
      }
    }

    td:nth-child(2) {
      width: 66.5px;

      &.two-column {
        padding-left: 8px;
        width: 118.5px;
      }
    }

    td:nth-child(3) {
      padding-left: 12px;
      text-align: left;
      width: 42px;

      &.two-column {
        padding-left: 16px;
        padding-right: 8px;
        width: 54px;
      }
    }

    td:nth-child(4) {
      width: 62px;
    }

    td:nth-child(5) {
      padding-left: 12px;
      text-align: left;
      width: 42px;
    }

    td:last-child {
      padding-right: 0px;
      width: 54.5px;
    }
  }
`;

const LastPriceTextWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 20px;
  line-height: 21px;
  padding: 0 10px;
`;

export {
  OrderbookText,
  OrderbookTextHover,
  TableSeparatorWrapper,
  TableBestBidOfferWrapper,
  OrderbookTableWrapper,
  CardHeader,
  Tag,
  OptionText,
  IconMoreOptions,
  RightIconMoreOptions,
  NotAvailableText,
  SecureTable,
  BalanceInfoWrapper,
  StyledIconWrapper,
  ValueWrapper,
  PendingMark,
  Wrapper,
  LastPriceTextWrapper,
};
